import {
  Card,
  Checkbox,
  Col,
  Row,
  Collapse,
  Tag,
  Typography,
  Space,
} from "antd";
import Flex from "components/shared-components/Flex";
import React from "react";
import { motion } from "framer-motion";

const { Panel } = Collapse;
const { Text } = Typography;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.05,
    },
  },
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15,
    scale: 0.98,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 400,
      damping: 30,
      duration: 0.4,
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, x: -8 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 30,
      duration: 0.3,
    },
  },
  hover: {
    scale: 1.015,
    y: -1,
    transition: {
      type: "spring",
      stiffness: 500,
      damping: 30,
      duration: 0.2,
    },
  },
};

const checkboxVariants = {
  checked: {
    scale: [1, 1.15, 1],
    transition: {
      duration: 0.25,
      ease: "easeOut",
    },
  },
  unchecked: {
    scale: 1,
    transition: {
      duration: 0.15,
      ease: "easeInOut",
    },
  },
};

const panelStyle = {
  backgroundColor: "#fff",
  borderRadius: "12px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
  marginBottom: "0",
  padding: "6px",
};
const CommionPermissonsAcc = ({
  UserPermission,
  handlePermissionChange,
  existPermission,
  selectAll,
  permissionGroups,
  groupedPermissions,
  getGroupStats,
  handleGroupSelect,
}) => {
  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <Row gutter={[24, 24]}>
        {Object.entries(permissionGroups).map(
          ([groupKey, groupConfig], index) => {
            const groupPermissions = groupedPermissions[groupKey] || [];
            if (groupPermissions.length === 0) return null;

            const stats = getGroupStats(groupKey);
            const isAllSelected =
              stats.selected === stats.total && stats.total > 0;
            const isPartiallySelected =
              stats.selected > 0 && stats.selected < stats.total;

            return (
              <Col xs={24} sm={24} md={12} lg={8} xl={8} key={groupKey}>
                <motion.div variants={itemVariants} style={{ height: "100%" }}>
                  <Collapse ghost size="small" style={{ height: "100%" }}>
                    <Panel
                      style={panelStyle}
                      key={groupKey}
                      header={
                        <Flex
                          alignItems="center"
                          justifyContent="space-between"
                          style={{ width: "100%" }}
                        >
                          <Space>
                            <span style={{ fontSize: "16px" }}>
                              {groupConfig.icon}
                            </span>
                            <Text strong>{groupConfig.name}</Text>
                          </Space>
                          {UserPermission?.includes(
                            "Assign User Permission"
                          ) && (
                            <motion.div
                              variants={checkboxVariants}
                              className="d-flex align-items-center"
                              animate={isAllSelected ? "checked" : "unchecked"}
                            >
                              <Tag color={groupConfig.color}>
                                {stats.selected}/{stats.total}
                              </Tag>
                              <Checkbox
                                checked={isAllSelected}
                                indeterminate={isPartiallySelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleGroupSelect(groupKey, e.target.checked);
                                }}
                                onClick={(e) => e.stopPropagation()}
                              >
                                Select All
                              </Checkbox>
                            </motion.div>
                          )}
                        </Flex>
                      }
                    >
                      <Row
                        gutter={[16, 0]}
                        style={{ maxHeight: "25vh", overflowY: "auto" }}
                      >
                        {groupPermissions.map((item, index) => (
                          <Col
                            xs={24}
                            sm={12}
                            lg={24}
                            xl={12}
                            key={item?.id || index}
                          >
                            <motion.div
                              variants={cardVariants}
                              initial="hidden"
                              animate="visible"
                              whileHover="hover"
                            >
                              <Card size="small" hoverable>
                                <Flex
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <div style={{ flex: 1 }}>
                                    <Text
                                      strong={
                                        existPermission?.includes(item?.id) ||
                                        selectAll
                                      }
                                    >
                                      {item.name}
                                    </Text>
                                  </div>
                                  {UserPermission?.includes(
                                    "Assign User Permission"
                                  ) && (
                                    <motion.div
                                      variants={checkboxVariants}
                                      animate={
                                        existPermission?.includes(item?.id) ||
                                        selectAll
                                          ? "checked"
                                          : "unchecked"
                                      }
                                    >
                                      <Checkbox
                                        onChange={(e) =>
                                          handlePermissionChange(e)
                                        }
                                        value={item?.id}
                                        checked={
                                          existPermission?.includes(item?.id) ||
                                          selectAll
                                        }
                                        id={`checkbox-${groupKey}-${index}`}
                                      />
                                    </motion.div>
                                  )}
                                </Flex>
                              </Card>
                            </motion.div>
                          </Col>
                        ))}
                      </Row>
                    </Panel>
                  </Collapse>
                </motion.div>
              </Col>
            );
          }
        )}

        {/* Render miscellaneous permissions if any */}
        {groupedPermissions.misc && groupedPermissions.misc.length > 0 && (
          <Col xs={24} sm={24} md={12} lg={8} xl={8} key="misc">
            <motion.div variants={itemVariants} style={{ height: "100%" }}>
              <Collapse ghost size="small" style={{ height: "100%" }}>
                <Panel
                  key="misc"
                  style={panelStyle}
                  header={
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      style={{ width: "100%" }}
                    >
                      <Space>
                        <Text strong>Other Permissions</Text>
                        <Tag color="default">
                          {getGroupStats("misc").selected}/
                          {getGroupStats("misc").total}
                        </Tag>
                      </Space>
                      {UserPermission?.includes("Assign User Permission") && (
                        <motion.div
                          variants={checkboxVariants}
                          animate={
                            getGroupStats("misc").selected ===
                              getGroupStats("misc").total &&
                            getGroupStats("misc").total > 0
                              ? "checked"
                              : "unchecked"
                          }
                        >
                          <Checkbox
                            checked={
                              getGroupStats("misc").selected ===
                                getGroupStats("misc").total &&
                              getGroupStats("misc").total > 0
                            }
                            indeterminate={
                              getGroupStats("misc").selected > 0 &&
                              getGroupStats("misc").selected <
                                getGroupStats("misc").total
                            }
                            onChange={(e) => {
                              e.stopPropagation();
                              handleGroupSelect("misc", e.target.checked);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Select All
                          </Checkbox>
                        </motion.div>
                      )}
                    </Flex>
                  }
                >
                  <Row
                    gutter={[16, 0]}
                    style={{ maxHeight: "25vh", overflowY: "auto" }}
                  >
                    {groupedPermissions.misc.map((item, index) => (
                      <Col
                        xs={24}
                        sm={12}
                        lg={24}
                        xl={12}
                        key={item?.id || index}
                      >
                        <motion.div
                          variants={cardVariants}
                          initial="hidden"
                          animate="visible"
                          whileHover="hover"
                        >
                          <Card size="small" hoverable>
                            <Flex
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <div style={{ flex: 1 }}>
                                <Text
                                  strong={
                                    existPermission?.includes(item?.id) ||
                                    selectAll
                                  }
                                >
                                  {item.name}
                                </Text>
                              </div>
                              {UserPermission?.includes(
                                "Assign User Permission"
                              ) && (
                                <motion.div
                                  variants={checkboxVariants}
                                  animate={
                                    existPermission?.includes(item?.id) ||
                                    selectAll
                                      ? "checked"
                                      : "unchecked"
                                  }
                                >
                                  <Checkbox
                                    onChange={(e) => handlePermissionChange(e)}
                                    value={item?.id}
                                    checked={
                                      existPermission?.includes(item?.id) ||
                                      selectAll
                                    }
                                    id={`checkbox-misc-${index}`}
                                  />
                                </motion.div>
                              )}
                            </Flex>
                          </Card>
                        </motion.div>
                      </Col>
                    ))}
                  </Row>
                </Panel>
              </Collapse>
            </motion.div>
          </Col>
        )}
      </Row>
    </motion.div>
  );
};

export default CommionPermissonsAcc;
